import React, { useState, useEffect } from 'react';
import Calendar from './Calendar';
import TimeSlotList from './TimeSlotList';

/**
 * AvailabilityViewer Component - Main container for availability selection
 * Fetches available time slots from API and manages state
 *
 * @param {Object} props
 * @param {Number} props.eventTypeId - ID of the EventType to fetch availability for
 * @param {String} props.timezone - Timezone for the student (default: Asia/Taipei)
 * @param {Array} props.timezoneOptions - Timezone options from backend
 * @param {Function} props.onSlotSelect - Callback when a slot is selected
 * @param {Function} props.onTimezoneChange - Callback when timezone changes
 */
const AvailabilityViewer = ({
  eventTypeId,
  fetchUrl,
  excludeBookingId,
  timezone = 'Asia/Taipei',
  timezoneOptions = [],
  onSlotSelect,
  onTimezoneChange,
  translations = {}
}) => {
  // State
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availabilityData, setAvailabilityData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [eventTypeInfo, setEventTypeInfo] = useState(null);

  // Fetch availability data when month or timezone changes
  useEffect(() => {
    fetchAvailability();
  }, [currentMonth, eventTypeId, fetchUrl, excludeBookingId, timezone]);

  const fetchAvailability = async () => {
    setLoading(true);
    setError(null);

    try {
      // Calculate date range for current month
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

      // Format dates as YYYY-MM-DD without timezone conversion
      const startYear = startDate.getFullYear();
      const startMonth = String(startDate.getMonth() + 1).padStart(2, '0');
      const startDay = String(startDate.getDate()).padStart(2, '0');
      const startDateStr = `${startYear}-${startMonth}-${startDay}`;

      const endYear = endDate.getFullYear();
      const endMonth = String(endDate.getMonth() + 1).padStart(2, '0');
      const endDay = String(endDate.getDate()).padStart(2, '0');
      const endDateStr = `${endYear}-${endMonth}-${endDay}`;

      // Build API URL
      const params = new URLSearchParams({
        start_date: startDateStr,
        end_date: endDateStr,
        timezone: timezone
      });

      if (excludeBookingId && excludeBookingId > 0) {
        params.set('exclude_booking_id', excludeBookingId);
      }

      // Only allow same-origin API paths to prevent open redirect
      const safeUrl = fetchUrl && fetchUrl.startsWith('/api/') ? fetchUrl : null;
      const apiUrl = safeUrl
        ? `${safeUrl}?${params.toString()}`
        : `/api/event_types/${eventTypeId}/availability?${params.toString()}`;

      const response = await fetch(apiUrl);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch availability');
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch availability');
      }

      setAvailabilityData(data.data);
      setEventTypeInfo(data.meta);

      // Auto-select first available date if no date is selected
      if (!selectedDate) {
        const firstAvailableDate = findFirstAvailableDate(data.data);
        if (firstAvailableDate) {
          setSelectedDate(firstAvailableDate);
        }
      }
    } catch (err) {
      console.error('Error fetching availability:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Find first available date in the data
  const findFirstAvailableDate = (data) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sortedDates = Object.keys(data)
      .filter(dateStr => {
        const date = new Date(dateStr);
        return date >= today && data[dateStr].length > 0;
      })
      .sort();

    if (sortedDates.length > 0) {
      return new Date(sortedDates[0]);
    }

    return null;
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null); // Clear slot selection when date changes
  };

  // Handle month change
  const handleMonthChange = (newMonth) => {
    setCurrentMonth(newMonth);
    setSelectedDate(null); // Clear selection when month changes
    setSelectedSlot(null);
  };

  // Handle slot selection
  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot.start);

    if (onSlotSelect) {
      onSlotSelect({
        date: selectedDate,
        slot: slot,
        eventTypeId: eventTypeId,
        timezone: timezone
      });
    }
  };

  // Get slots for selected date
  const getSelectedDateSlots = () => {
    if (!selectedDate) return [];

    // Format date without timezone conversion to match API response keys
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return availabilityData[dateStr] || [];
  };

  // Error state
  if (error) {
    return (
      <div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-lg font-medium text-red-900">{translations.error_title || 'Load failed'}</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={fetchAvailability}
                className="mt-3 text-sm font-medium text-red-600 hover:text-red-800"
              >
                {translations.retry || 'Retry'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div>
      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar */}
        <div>
          <Calendar
            availabilityData={availabilityData}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            currentMonth={currentMonth}
            onMonthChange={handleMonthChange}
            translations={translations.calendar || {}}
          />
        </div>

        {/* Time Slots with Timezone Selector */}
        <div>
          {/* Compact Timezone Selector — only shown when options are provided */}
          {timezoneOptions.length > 0 && (
            <div className="mb-4">
              <select
                id="timezone-select"
                value={timezone}
                onChange={(e) => onTimezoneChange && onTimezoneChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {timezoneOptions.map((tz) => (
                  <option key={tz.id} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <TimeSlotList
            slots={getSelectedDateSlots()}
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
            onSlotSelect={handleSlotSelect}
            duration={eventTypeInfo?.duration_minutes}
            loading={loading}
            translations={translations.time_slots || {}}
          />
        </div>
      </div>
    </div>
  );
};

export default AvailabilityViewer;
